import { css } from '@emotion/css';
import styled from '~/utils/styled';
// ---- external imports ----

import Asset from '~/containers/GameAsset';
import { PointerCursor, TextCursor, DeniedCursor } from '~/containers/GameAsset/cursors';
import {
	FieldInput as BaseFieldInput,
	ErrorMessage as BaseErrorMessage,
	PrimaryButton,
	createPrimaryButtonText
} from '~/authentication/AuthAssets';
import inputFieldDownImg from '~/assets/images/auth/dropdown/input-field-down.png';
import menuFirstImg from '~/assets/images/auth/dropdown/menu-first.png';
import menuItemImg from '~/assets/images/auth/dropdown/menu-item.png';
import menuLastImg from '~/assets/images/auth/dropdown/menu-last.png';
// ---- internal imports ----


// region [Projection Constants]
export const TITLE_PROJECTION = { width: 821 };
export const FULL_INPUT_PROJECTION = { width: 632, height: 54 };
export const HALF_INPUT_PROJECTION = { width: 309, height: 54 };
const HALF_FORM_WIDTH_PROJECTION = { width: 309 };
const DROPDOWN_SELECTED_PROJECTION = { width: 520 };
const START_BTN_TEXT_PROJECTION = { width: 149 };
const MENU_CONTAINER_PROJECTION = { width: 248 };
// endregion

// region [Components]

export const HalfFieldLabel = styled(Asset.Text.withProps({
	fontSize: 16,
	projection: HALF_INPUT_PROJECTION,
}))`
	font-family: "NotoSans";
	color: #fff5b0;
`;

export const HalfInputBackground = styled(Asset.Backdrop.withProps({
	projection: HALF_INPUT_PROJECTION,
}))`
	background-color: rgba(0, 0, 0, 0.1);
	border-radius: 12px;
	box-shadow: inset 0 0 17px 0 rgba(0, 0, 0, 0.25);
`;

// Full-width input without eye button — overrides right padding from the shared base
export const FieldInput = styled(BaseFieldInput)`
	padding: 0 16px;
`;

export const HalfFieldInput = styled(Asset.Input.withProps({
	projection: HALF_INPUT_PROJECTION,
	fontSize: 32,
}))`
	font-family: "NotoSans SemiBold";
	color: #fff5b0;
	padding: 0 16px;
	border-radius: 12px;
	box-sizing: border-box;
	${TextCursor};

	::placeholder {
		color: rgba(114, 37, 12, 1);
		font-family: 'NotoSans SemiBold'
	};

	&:focus {
		border-color: rgba(255, 245, 176, 1);
		box-shadow: inset 0 0 17px 0 rgba(0, 0, 0, 0.25), 0 0 12px 0 rgba(255, 245, 176, 1);
		outline: none;
	};

	&:disabled {
		${DeniedCursor};
	};

	${({ hasError }) => hasError && `
		border: 4px solid #D63B25;
		box-shadow: inset 0 0 17px 0 rgba(0, 0, 0, 0.25);
	`}
`;

export const HalfErrorMessage = styled(Asset.Text.withProps({
	fontSize: 20,
	projection: HALF_FORM_WIDTH_PROJECTION,
}))`
	text-shadow: 0px 2px 4px rgba(0, 0, 0, 0.25);
	font-family: "NotoSans SemiBold";
	color: #ffffff;
`;

// Registration-local restyle of the shared ErrorMessage — white + drop-shadow (Figma 8591:6410/8591:6407/3312:12005/8591:6405)
export const ErrorMessage = styled(BaseErrorMessage)`
	font-family: "NotoSans SemiBold";
	color: #ffffff;
	text-shadow: 0px 2px 4px rgba(0, 0, 0, 0.25);
`;

// Bold clause within HalfErrorMessage/ErrorMessage — nested inline span, not a positioned Asset.Text
export const errorEmphasisClass = css`
	font-family: "NotoSans ExtraBold";
`;

export const DropdownButton = styled(Asset.Button.withProjection(inputFieldDownImg))`
	border: none;
	padding: 0;
	z-index: 10;
`;

export const DropdownMenuContainer = styled(Asset.DivContainer.withProps({
	projection: MENU_CONTAINER_PROJECTION,
}))`
	display: flex;
	flex-direction: column;
	z-index: 11;
`;

export const DropdownMenuItem = styled.button`
	display: flex;
	align-items: center;
	width: 100%;
	height: ${({ isFirst, isLast }) => isFirst ? 32 : isLast ? 40 : 22}px;
	background-image: url(${({ isFirst, isLast }) =>
		isFirst ? menuFirstImg.src :
		isLast  ? menuLastImg.src  :
		menuItemImg.src
	});
	background-size: 100% 100%;
	background-color: transparent;
	border: none;
	padding-left: 28px;
	padding-top: ${({ isFirst }) => isFirst ? '6px' : '0'};
	padding-bottom: ${({ isLast }) => isLast ? '12px' : '0'};
	color: ${({ isSelected }) => isSelected ? '#F9C24D' : '#72250c'};
	font-family: 'NotoSans SemiBold', 'Noto Sans', sans-serif;
	font-size: 14px;
	${PointerCursor};

	&:disabled {
		${DeniedCursor};
	};
`;

export const DropdownSelectedText = styled(Asset.Input.withProps({
	fontSize: 32,
	projection: DROPDOWN_SELECTED_PROJECTION,
}))`
	font-family: "NotoSans SemiBold";
	color: #fff5b0;
	padding: 0 16px;
	pointer-events: none;
	border-radius: 12px;

	::placeholder {
		color: rgba(114, 37, 12, 1);
		font-family: 'NotoSans SemiBold'
	};
`;

export const StartButton = PrimaryButton;

export const StartButtonText = createPrimaryButtonText(START_BTN_TEXT_PROJECTION);

export const FormWrapper = styled.form`
	fieldset {
		border: none;
		padding: 0;
		margin: 0;
	}
`;

// endregion

